/**
 * Admin 사이트 > 아티스트 CRUD. 라우트 admin.php site.artists 와 동기화.
 * create/edit: Quill 에디터(국문/영문), 소개 탭, 폼 제출 시 description 동기화.
 */
import { Logger } from "../../utils/Logger";
import { QuillEditor, stripBase64Images } from "../../components/QuillEditor";

function noop(controller: string, action: string): void {
    Logger.debug(`${controller}.${action} (no-op)`);
}

const CTRL_TAG = "AdminArtistController";

function initArtistIntroEditors(form: HTMLFormElement): void {
    const editorKoEl = document.getElementById("artist-description-editor-ko");
    const editorEnEl = document.getElementById("artist-description-editor-en");
    const descInput = document.getElementById("artist-description-input") as HTMLInputElement | null;
    const descEnInput = document.getElementById("artist-description-en-input") as HTMLInputElement | null;
    if (!editorKoEl || !editorEnEl || !descInput || !descEnInput) return;

    const uploadImageUrl = form.dataset.uploadImageUrl ?? "";
    const csrf = (form.querySelector('input[name="_token"]') as HTMLInputElement | null)?.value ?? "";

    let initialKo = "";
    let initialEn = "";
    const initEl = document.getElementById("artist-intro-initial");
    if (initEl?.textContent) {
        try {
            const initial = JSON.parse(initEl.textContent) as { description?: string; description_en?: string };
            initialKo = initial.description ?? "";
            initialEn = initial.description_en ?? "";
        } catch {
            // ignore
        }
    }

    const uploadConfig =
        uploadImageUrl && csrf
            ? {
                  uploadUrl: uploadImageUrl,
                  csrf,
                  fileKey: "image" as const,
                  getUrl: (json: Record<string, unknown>) =>
                      (json?.data as Record<string, unknown>)?.url as string | undefined,
              }
            : undefined;

    const quillKo = new QuillEditor(editorKoEl, {
        placeholder: "소개글을 입력하세요. 이미지는 툴바 이미지 버튼으로 넣을 수 있습니다.",
        initialHtml: initialKo,
        upload: uploadConfig,
    });
    quillKo.init();

    const quillEn = new QuillEditor(editorEnEl, {
        placeholder: "소개글을 입력하세요. 이미지는 툴바 이미지 버튼으로 넣을 수 있습니다.",
        initialHtml: initialEn,
        upload: uploadConfig,
    });
    quillEn.init();

    const introTabBtns = document.querySelectorAll(".admin-form__section--tabs .admin-notice-tabs__tab");
    const introPanels = document.querySelectorAll("#artist-intro-panel-ko, #artist-intro-panel-en");

    const showIntroPanel = (tabKey: string): void => {
        introPanels.forEach((p) => {
            (p as HTMLElement).classList.toggle("admin-notice-panel--hidden", p.id !== "artist-intro-panel-" + tabKey);
        });
        introTabBtns.forEach((btn) => {
            const key = btn.getAttribute("data-tab");
            btn.classList.toggle("admin-notice-tabs__tab--active", key === tabKey);
            btn.setAttribute("aria-selected", key === tabKey ? "true" : "false");
        });
    };
    introTabBtns.forEach((btn) => {
        btn.addEventListener("click", () => showIntroPanel(btn.getAttribute("data-tab") ?? "ko"));
    });

    form.addEventListener(
        "submit",
        () => {
            descInput.value = stripBase64Images(quillKo.getHtml());
            descEnInput.value = stripBase64Images(quillEn.getHtml());
        },
        true
    );
}

function escapeHtml(s: string): string {
    return s
        .replace(/&/g, "&amp;")
        .replace(/</g, "&lt;")
        .replace(/>/g, "&gt;")
        .replace(/"/g, "&quot;");
}

type ArtistEditAjaxDone = (res: { result: string; msg?: string; item?: Record<string, unknown> }) => void;

function initArtistEditTabsAndAjax(form: HTMLFormElement): void {
    const artistId = form.dataset.artistId ?? "";
    const updateUrl = form.dataset.updateUrl ?? "";
    const baseUrl = updateUrl
        .replace(/\/[^/]+\/update\/?$/, "")
        .replace(/\/[0-9]+\/?$/, "")
        .concat("/");

    const tabs = form.closest(".admin-page")?.querySelectorAll(".admin-artist-tabs__tab") ?? [];
    const panels = form.closest(".admin-page")?.querySelectorAll(".admin-artist-panel") ?? [];

    function showPanel(tabKey: string): void {
        panels.forEach((p) => {
            const id = p.id;
            if (id?.startsWith("artist-panel-")) {
                const key = id.replace("artist-panel-", "");
                p.classList.toggle("admin-artist-panel--hidden", key !== tabKey);
            }
        });
        tabs.forEach((btn) => {
            const key = btn.getAttribute("data-tab");
            btn.classList.toggle("admin-artist-tabs__tab--active", key === tabKey);
            btn.setAttribute("aria-selected", key === tabKey ? "true" : "false");
        });
    }
    tabs.forEach((btn) => {
        btn.addEventListener("click", () => showPanel(btn.getAttribute("data-tab") ?? ""));
    });

    function ajaxPost(url: string, data: Record<string, string>, done: ArtistEditAjaxDone): void {
        const token = (form.querySelector('input[name="_token"]') as HTMLInputElement | null)?.value ?? "";
        const fd = new FormData();
        fd.append("_token", token);
        for (const k of Object.keys(data)) fd.append(k, data[k]);
        fetch(url, {
            method: "POST",
            body: fd,
            headers: { "X-Requested-With": "XMLHttpRequest", Accept: "application/json" },
        })
            .then((r) => r.json())
            .then(done)
            .catch(() => done({ result: "fail", msg: "요청 처리 중 오류가 발생했습니다." }));
    }

    const musicAddBtn = document.getElementById("artist-music-add");
    const musicSelect = document.getElementById("artist-music-select") as HTMLSelectElement | null;
    const musicsTbody = document.getElementById("artist-musics-tbody");

    if (musicAddBtn && musicSelect && musicsTbody) {
        function alreadyInTable(musicId: number): boolean {
            return musicsTbody!.querySelector(`tr[data-music-id="${musicId}"]`) !== null;
        }

        function detachMusicClick(this: HTMLElement): void {
            const musicId = this.getAttribute("data-music-id");
            if (!musicId || !confirm("이 음원 연결을 해제하시겠습니까?")) return;
            const tr = musicsTbody!.querySelector(`tr[data-music-id="${musicId}"]`);
            const titleCell = tr?.querySelector("td:first-child");
            const dateCell = tr?.querySelector("td:nth-child(2)");
            const title = titleCell?.textContent ?? "";
            const date = dateCell?.textContent ?? "-";
            ajaxPost(`${baseUrl}${artistId}/musics/${musicId}/detach`, {}, (res) => {
                if (res.result === "success" && tr) {
                    tr.remove();
                    const opt = document.createElement("option");
                    opt.value = musicId;
                    opt.textContent = `${title} (${date})`;
                    musicSelect!.appendChild(opt);
                } else {
                    alert(res.msg ?? "해제에 실패했습니다.");
                }
            });
        }

        musicAddBtn.addEventListener("click", () => {
            const selected = Array.from(musicSelect.selectedOptions ?? []);
            const toAdd = selected.filter((opt) => {
                const id = parseInt(opt.value, 10);
                return id && !alreadyInTable(id);
            });
            if (toAdd.length === 0) {
                alert("추가할 음원을 선택하세요. (이미 연결된 음원은 제외됩니다.)");
                return;
            }
            let idx = 0;
            function addOne(): void {
                if (idx >= toAdd.length) return;
                const opt = toAdd[idx];
                const musicId = parseInt(opt.value, 10);
                idx += 1;
                if (!musicId) {
                    addOne();
                    return;
                }
                ajaxPost(`${baseUrl}${artistId}/musics`, { music_id: String(musicId) }, (res) => {
                    if (res.result === "success" && res.item) {
                        const item = res.item as { title?: string; release_date?: string };
                        const releaseDate = item.release_date != null ? String(item.release_date) : "-";
                        const title = item.title != null ? escapeHtml(String(item.title)) : "";
                        const tr = document.createElement("tr");
                        tr.setAttribute("data-music-id", String(musicId));
                        tr.innerHTML = `<td>${title}</td><td>${escapeHtml(releaseDate)}</td><td><button type="button" class="admin-btn admin-btn--sm admin-artist-detach-music" data-music-id="${musicId}">삭제</button></td>`;
                        musicsTbody!.appendChild(tr);
                        tr.querySelector(".admin-artist-detach-music")?.addEventListener("click", detachMusicClick);
                        opt.remove();
                    }
                    addOne();
                });
            }
            addOne();
        });

        musicsTbody.querySelectorAll(".admin-artist-detach-music").forEach((btn) => {
            btn.addEventListener("click", detachMusicClick);
        });
    }

    const editingPartnerIdEl = document.getElementById("editing-partner-id") as HTMLInputElement | null;
    const partnerAddBtn = document.getElementById("artist-partner-add");
    const partnersTbody = document.getElementById("artist-partners-tbody");

    if (partnerAddBtn && partnersTbody) {
        function editPartnerClick(this: HTMLElement): void {
            const pid = this.getAttribute("data-partner-id");
            if (!pid) return;
            fetch(`${baseUrl}${artistId}/partners/${pid}`, {
                headers: { Accept: "application/json", "X-Requested-With": "XMLHttpRequest" },
            })
                .then((r) => r.json())
                .then((res: { result: string; msg?: string; item?: Record<string, unknown> }) => {
                    if (res.result === "success" && res.item) {
                        const item = res.item;
                        const titleEl = document.getElementById("partner-title") as HTMLInputElement | null;
                        const contentEl = document.getElementById("partner-content") as HTMLInputElement | null;
                        const linkEl = document.getElementById("partner-link_url") as HTMLInputElement | null;
                        if (titleEl) titleEl.value = (item.title as string) ?? "";
                        if (contentEl) contentEl.value = (item.content as string) ?? "";
                        if (linkEl) linkEl.value = (item.link_url as string) ?? "";
                        if (editingPartnerIdEl) editingPartnerIdEl.value = pid;
                        if (partnerAddBtn) partnerAddBtn.textContent = "저장";
                        const uploaderRoot = document.querySelector(
                            '#artist-panel-partners [data-file-uploader][data-attachment-name="partner_thumb_attachment_id"]'
                        ) as { _fileUploaderComponent?: { setExistingPreview?: (url: string) => void; reset?: () => void } } | null;
                        if (uploaderRoot?._fileUploaderComponent) {
                            if (item.thumb_url) {
                                uploaderRoot._fileUploaderComponent.setExistingPreview?.(String(item.thumb_url));
                            } else {
                                uploaderRoot._fileUploaderComponent.reset?.();
                            }
                        }
                    } else {
                        alert(res.msg ?? "파트너 정보를 불러올 수 없습니다.");
                    }
                })
                .catch(() => alert("요청 처리 중 오류가 발생했습니다."));
        }

        function deletePartnerClick(this: HTMLElement): void {
            const pid = this.getAttribute("data-partner-id");
            if (!pid || !confirm("이 파트너를 삭제하시겠습니까?")) return;
            ajaxPost(`${baseUrl}${artistId}/partners/${pid}/destroy`, {}, (res) => {
                if (res.result === "success") {
                    const tr = partnersTbody.querySelector(`tr[data-partner-id="${pid}"]`);
                    if (tr) tr.remove();
                } else {
                    alert(res.msg ?? "삭제에 실패했습니다.");
                }
            });
        }

        partnerAddBtn.addEventListener("click", () => {
            const titleEl = document.getElementById("partner-title") as HTMLInputElement | null;
            const title = (titleEl?.value ?? "").trim();
            if (!title) {
                alert("파트너 이름을 입력하세요.");
                return;
            }
            const contentEl = document.getElementById("partner-content") as HTMLInputElement | null;
            const linkEl = document.getElementById("partner-link_url") as HTMLInputElement | null;
            const thumbInput = document.querySelector<HTMLInputElement>('input[name="partner_thumb_attachment_id"]');
            const partnerThumbId = thumbInput?.value ?? "";
            const payload: Record<string, string> = {
                title,
                content: contentEl?.value ?? "",
                link_url: linkEl?.value ?? "",
            };
            if (partnerThumbId) payload.partner_thumb_attachment_id = partnerThumbId;
            const pid = editingPartnerIdEl?.value ?? "";
            const url = pid ? `${baseUrl}${artistId}/partners/${pid}` : `${baseUrl}${artistId}/partners`;
            ajaxPost(url, payload, (res) => {
                if (res.result !== "success" || !res.item) {
                    alert(res.msg ?? (pid ? "수정에 실패했습니다." : "추가에 실패했습니다."));
                    return;
                }
                const item = res.item as Record<string, unknown>;
                const thumbCell = item.thumb_url
                    ? `<td><img src="${escapeHtml(String(item.thumb_url))}" alt="" class="admin-table__thumb" width="48" height="48"></td>`
                    : "<td>-</td>";
                const actionsCell = `<td><button type="button" class="admin-btn admin-btn--sm admin-artist-edit-partner" data-partner-id="${item.id}">수정</button><button type="button" class="admin-btn admin-btn--sm admin-artist-delete-partner" data-partner-id="${item.id}">삭제</button></td>`;
                const titleEsc = escapeHtml((item.title as string) ?? "");
                const contentEsc = escapeHtml(String((item.content as string) ?? "").substring(0, 50));
                const linkEsc = item.link_url
                    ? `<a href="${escapeHtml(String(item.link_url))}" target="_blank" rel="noopener">링크</a>`
                    : "-";
                if (pid) {
                    const tr = partnersTbody.querySelector(`tr[data-partner-id="${pid}"]`);
                    if (tr) {
                        tr.innerHTML = thumbCell + `<td>${titleEsc}</td><td>${contentEsc}</td><td>${linkEsc}</td>` + actionsCell;
                        tr.querySelector(".admin-artist-edit-partner")?.addEventListener("click", editPartnerClick);
                        tr.querySelector(".admin-artist-delete-partner")?.addEventListener("click", deletePartnerClick);
                    }
                } else {
                    const tr = document.createElement("tr");
                    tr.setAttribute("data-partner-id", String(item.id));
                    tr.innerHTML = thumbCell + `<td>${titleEsc}</td><td>${contentEsc}</td><td>${linkEsc}</td>` + actionsCell;
                    partnersTbody.appendChild(tr);
                    tr.querySelector(".admin-artist-edit-partner")?.addEventListener("click", editPartnerClick);
                    tr.querySelector(".admin-artist-delete-partner")?.addEventListener("click", deletePartnerClick);
                }
                if (titleEl) titleEl.value = "";
                if (contentEl) contentEl.value = "";
                if (linkEl) linkEl.value = "";
                if (editingPartnerIdEl) editingPartnerIdEl.value = "";
                if (partnerAddBtn) partnerAddBtn.textContent = "추가";
                const uploaderRoot = document.querySelector(
                    '#artist-panel-partners [data-file-uploader][data-attachment-name="partner_thumb_attachment_id"]'
                ) as { _fileUploaderComponent?: { reset?: () => void } } | null;
                uploaderRoot?._fileUploaderComponent?.reset?.();
            });
        });

        partnersTbody.querySelectorAll(".admin-artist-edit-partner").forEach((btn) => btn.addEventListener("click", editPartnerClick));
        partnersTbody.querySelectorAll(".admin-artist-delete-partner").forEach((btn) => btn.addEventListener("click", deletePartnerClick));
    }

    const editingPerformanceIdEl = document.getElementById("editing-performance-id") as HTMLInputElement | null;
    const performanceAddBtn = document.getElementById("artist-performance-add");
    const performancesTbody = document.getElementById("artist-performances-tbody");

    if (performanceAddBtn && performancesTbody) {
        function editPerformanceClick(this: HTMLElement): void {
            const pid = this.getAttribute("data-performance-id");
            if (!pid) return;
            const tr = performancesTbody.querySelector(`tr[data-performance-id="${pid}"]`);
            if (!tr) return;
            const cells = tr.querySelectorAll("td");
            const title = (cells[0]?.textContent ?? "").trim();
            const dateStr = (cells[1]?.textContent ?? "").trim();
            const titleEl = document.getElementById("performance-title") as HTMLInputElement | null;
            const dateEl = document.getElementById("performance-date") as HTMLInputElement | null;
            if (titleEl) titleEl.value = title;
            if (dateEl) dateEl.value = dateStr === "-" ? "" : dateStr;
            if (editingPerformanceIdEl) editingPerformanceIdEl.value = pid;
            if (performanceAddBtn) performanceAddBtn.textContent = "저장";
        }

        function deletePerformanceClick(this: HTMLElement): void {
            const pid = this.getAttribute("data-performance-id");
            if (!pid || !confirm("이 공연을 삭제하시겠습니까?")) return;
            ajaxPost(`${baseUrl}${artistId}/performances/${pid}/destroy`, {}, (res) => {
                if (res.result === "success") {
                    const tr = performancesTbody.querySelector(`tr[data-performance-id="${pid}"]`);
                    if (tr) tr.remove();
                } else {
                    alert(res.msg ?? "삭제에 실패했습니다.");
                }
            });
        }

        performanceAddBtn.addEventListener("click", () => {
            const titleEl = document.getElementById("performance-title") as HTMLInputElement | null;
            const title = (titleEl?.value ?? "").trim();
            if (!title) {
                alert("공연명을 입력하세요.");
                return;
            }
            const dateEl = document.getElementById("performance-date") as HTMLInputElement | null;
            const performance_date = dateEl?.value ?? "";
            const pid = editingPerformanceIdEl?.value ?? "";
            const url = pid ? `${baseUrl}${artistId}/performances/${pid}` : `${baseUrl}${artistId}/performances`;
            ajaxPost(url, { title, performance_date }, (res) => {
                if (res.result !== "success" || !res.item) {
                    alert(res.msg ?? (pid ? "수정에 실패했습니다." : "추가에 실패했습니다."));
                    return;
                }
                const item = res.item as Record<string, unknown>;
                const actionsCell = `<td><button type="button" class="admin-btn admin-btn--sm admin-artist-edit-performance" data-performance-id="${item.id}">수정</button><button type="button" class="admin-btn admin-btn--sm admin-artist-delete-performance" data-performance-id="${item.id}">삭제</button></td>`;
                const titleEsc = escapeHtml((item.title as string) ?? "");
                const dateVal = (item.performance_date as string) ?? "-";
                if (pid) {
                    const tr = performancesTbody.querySelector(`tr[data-performance-id="${pid}"]`);
                    if (tr) {
                        tr.innerHTML = `<td>${titleEsc}</td><td>${dateVal}</td>${actionsCell}`;
                        tr.querySelector(".admin-artist-edit-performance")?.addEventListener("click", editPerformanceClick);
                        tr.querySelector(".admin-artist-delete-performance")?.addEventListener("click", deletePerformanceClick);
                    }
                } else {
                    const tr = document.createElement("tr");
                    tr.setAttribute("data-performance-id", String(item.id));
                    tr.innerHTML = `<td>${titleEsc}</td><td>${dateVal}</td>${actionsCell}`;
                    performancesTbody.appendChild(tr);
                    tr.querySelector(".admin-artist-edit-performance")?.addEventListener("click", editPerformanceClick);
                    tr.querySelector(".admin-artist-delete-performance")?.addEventListener("click", deletePerformanceClick);
                }
                if (titleEl) titleEl.value = "";
                if (dateEl) dateEl.value = "";
                if (editingPerformanceIdEl) editingPerformanceIdEl.value = "";
                if (performanceAddBtn) performanceAddBtn.textContent = "추가";
            });
        });

        performancesTbody.querySelectorAll(".admin-artist-edit-performance").forEach((btn) => btn.addEventListener("click", editPerformanceClick));
        performancesTbody.querySelectorAll(".admin-artist-delete-performance").forEach((btn) => btn.addEventListener("click", deletePerformanceClick));
    }
}

export const AdminArtistController = {
    index(): void {
        noop(CTRL_TAG, "index");
    },
    create(): void {
        const form = document.getElementById("artist-create-form") as HTMLFormElement | null;
        if (form) initArtistIntroEditors(form);
    },
    store(): void {
        noop(CTRL_TAG, "store");
    },
    edit(): void {
        const form = document.getElementById("artist-edit-form") as HTMLFormElement | null;
        if (form) {
            initArtistIntroEditors(form);
            initArtistEditTabsAndAjax(form);
        }
    },
    update(): void {
        noop(CTRL_TAG, "update");
    },
    destroy(): void {
        noop(CTRL_TAG, "destroy");
    },
    storePartner(): void {
        noop(CTRL_TAG, "storePartner");
    },
    getPartner(): void {
        noop(CTRL_TAG, "getPartner");
    },
    updatePartner(): void {
        noop(CTRL_TAG, "updatePartner");
    },
    destroyPartner(): void {
        noop(CTRL_TAG, "destroyPartner");
    },
    storePerformance(): void {
        noop(CTRL_TAG, "storePerformance");
    },
    updatePerformance(): void {
        noop(CTRL_TAG, "updatePerformance");
    },
    destroyPerformance(): void {
        noop(CTRL_TAG, "destroyPerformance");
    },
    attachMusic(): void {
        noop(CTRL_TAG, "attachMusic");
    },
    detachMusic(): void {
        noop(CTRL_TAG, "detachMusic");
    },
};
